import { Button } from "@repo/ui";
import { Label } from "@repo/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui";
import { Alert, AlertDescription } from "@repo/ui";
import { useAuth } from "@stardeck/client-auth";
import { useState } from "react";

import { useWithAuth } from "@/lib/queries/auth-query";

export default function AuthTest() {
  const { user, isLoading: isPending, signOut, signIn } = useAuth();
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useWithAuth();

  const handleSignIn = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      await signIn();
    } catch (error) {
      setMessage(`Sign in error: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      await signOut();
      setMessage("Signed out successfully!");
    } catch (error) {
      setMessage(`Sign out error: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isPending) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <p>Loading session...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Authentication Test</CardTitle>
            <CardDescription>
              Test iframe-based authentication system for sign in and sign out functionality.
            </CardDescription>
          </CardHeader>
        </Card>

        {message && (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {user ? (
          <Card>
            <CardHeader>
              <CardTitle>Welcome, {user.name || user.email}!</CardTitle>
              <CardDescription>You are currently signed in.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>User ID</Label>
                  <p className="text-sm text-muted-foreground">{user.id}</p>
                </div>
                <div>
                  <Label>Email</Label>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
                <div>
                  <Label>Role</Label>
                  <p className="text-sm text-muted-foreground">{user.role || "user"}</p>
                </div>
                <Button onClick={handleSignOut} disabled={isLoading} variant="outline">
                  {isLoading ? "Signing out..." : "Sign Out"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Sign In</CardTitle>
              <CardDescription>Choose your preferred sign-in method</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={handleSignIn} disabled={isLoading} className="w-full">
                {isLoading ? "Requesting..." : "Sign In"}
              </Button>
              <p className="text-sm text-muted-foreground">
                Authentication will be handled by the parent app via iframe communication or
                redirect.
              </p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Session Debug Info</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-4 rounded overflow-auto">
              {JSON.stringify(user, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
